/**
 * L'attestation d'hébergement et autorisation de domiciliation.
 *
 * UN SEUL DOCUMENT LÀ OÙ D'AUTRES EN ANNONCENT DEUX. Le dossier d'un
 * entrepreneur hébergé chez un proche réclame « une attestation d'hébergement
 * et une copie d'un justificatif de domicile de moins de 3 mois aux nom et
 * prénom de la personne qui héberge » (INPI). Une attestation qui dirait
 * seulement « je l'héberge » sans autoriser la domiciliation de l'entreprise
 * laisserait le dossier à moitié fait, et deux papiers séparés obligeraient à
 * faire signer deux fois la même personne. Celui-ci dit les deux.
 *
 * Sa base est le modèle d'attestation d'hébergement publié par la DILA
 * (service-public.gouv.fr, vérifié le 26 juin 2024), dont il reprend la
 * structure et les champs : qui héberge, qui est hébergé, depuis quand, à
 * quelle adresse. La phrase d'autorisation s'y ajoute, parce qu'aucun modèle
 * officiel ne couvre la domiciliation d'une entreprise chez un particulier.
 *
 * CE QU'IL N'AUTORISE PAS, ET POURQUOI C'EST VOULU. Domicilier n'est pas
 * exercer. L'adresse du siège est une adresse administrative ; recevoir des
 * clients ou installer un atelier chez un tiers relève du bail, du règlement de
 * copropriété et, dans les grandes villes, du code de la construction. Le
 * document ne dit donc rien de l'exercice, et la page l'explique plutôt que de
 * faire signer à un proche une autorisation qu'il ne comprendrait qu'à moitié.
 *
 * CELUI QUI SIGNE N'EST PAS CELUI QUI REMPLIT. C'est l'hébergeant qui atteste
 * et qui signe, l'entrepreneur ne fait que préparer le document. D'où l'absence
 * de pavé de signature dans le parcours : y coller la signature de session,
 * celle de l'entrepreneur, produirait un faux.
 */

import {
    dateEnToutesLettres,
    identite,
    lieuDeNaissance,
    lignesDAdresse,
    nettoyer,
} from "./documentText.js";

/**
 * Où l'entreprise est domiciliée, et ce que le dossier demande alors.
 *
 * Seul l'hébergement chez un tiers appelle une attestation. Les deux autres
 * cas se justifient par des pièces que l'entrepreneur a déjà, et le dire évite
 * de joindre au dossier un document dont personne n'a besoin.
 */
export function concerne(reponses) {
    const lieu = reponses?.lieu;
    if (!lieu) return { concerne: null, motif: null };
    if (lieu === "proche") return { concerne: true, motif: null };
    return { concerne: false, motif: lieu };
}

/**
 * Ce qui manque, ce qui est refusé, et ce qui mérite un mot sans rien bloquer.
 *
 * `manques` et `invalides` ne disent pas la même chose, et les confondre a
 * produit le pire message de la rubrique : « il manque la date de naissance de
 * votre hébergeant » devant un champ visiblement rempli. Une année tapée trop
 * vite dans un champ de date donne « 0001 », ce qui est une date présente et
 * lisible, mais un âge de deux mille ans. Le lecteur doit lire « vérifiez
 * l'année », pas « c'est vide ».
 */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["lieu"], invalides, alertes };
    }

    const requis = [
        // Deux civilités, deux accords : celle de l'hébergeant ouvre la lettre
        // (« Je soussignée »), celle de l'hébergé accorde le reste.
        ["hoteCivilite", r.hoteCivilite],
        ["hotePrenoms", r.hotePrenoms],
        ["hoteNom", r.hoteNom],
        ["hoteVilleDeNaissance", r.hoteVilleDeNaissance],
        ["civilite", r.civilite],
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["villeDeNaissance", r.villeDeNaissance],
        ["voie", r.voie],
        ["commune", r.commune],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const cp = nettoyer(r.codePostal);
    if (!cp) manques.push("codePostal");
    else if (!/^\d{5}$/.test(cp)) invalides.push({ champ: "codePostal", motif: "format" });

    for (const cle of ["hoteDateDeNaissance", "dateDeNaissance", "depuisLe", "dateDeSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!dateEnToutesLettres(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    const ref = new Date(aujourdhui);
    for (const cle of ["hoteDateDeNaissance", "dateDeNaissance"]) {
        if (!dateEnToutesLettres(r[cle])) continue;
        const ans = (ref - new Date(`${r[cle]}T00:00:00Z`)) / (365.2425 * 24 * 3600 * 1000);
        if (ans < 0 || ans > 110) invalides.push({ champ: cle, motif: "ageImpossible" });
    }

    // Un hébergement qui commencerait demain n'atteste de rien aujourd'hui.
    if (dateEnToutesLettres(r.depuisLe) && new Date(`${r.depuisLe}T00:00:00Z`) > ref) {
        invalides.push({ champ: "depuisLe", motif: "futur" });
    }

    // Deux fois la même personne, c'est une erreur de saisie dans presque tous
    // les cas, mais un père et son fils peuvent porter les mêmes prénom et nom :
    // on prévient, on ne bloque pas.
    const hote = identite({ prenoms: r.hotePrenoms, nom: r.hoteNom });
    const heberge = identite({ prenoms: r.prenoms, nom: r.nom });
    if (hote && hote === heberge) alertes.push("memePersonne");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * Le document, en blocs, prêt à être posé sur une page ou dans un PDF.
 *
 * Rend `null` tant qu'il manque une réponse : un document à trous ne se
 * télécharge pas, et l'appelant n'a pas à décider ce qu'il fait d'un trou.
 */
export function composerAutorisation(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet, aujourdhui).valide) return null;

    const hoteFeminin = r.hoteCivilite === "madame";
    const hebergeFeminin = r.civilite === "madame";

    const hote = identite({ prenoms: r.hotePrenoms, nom: r.hoteNom });
    const heberge = identite({ prenoms: r.prenoms, nom: r.nom });

    const naissanceHote = lieuDeNaissance({
        villeDeNaissance: r.hoteVilleDeNaissance,
        paysDeNaissance: r.hotePaysDeNaissance,
    });
    const naissanceHeberge = lieuDeNaissance(r);

    return {
        titre: "Attestation d'hébergement et autorisation de domiciliation",
        blocs: [
            {
                type: "paragraphe",
                texte:
                    `Je soussign${hoteFeminin ? "ée" : "é"} ${hote}, ` +
                    `n${hoteFeminin ? "ée" : "é"} le ${dateEnToutesLettres(r.hoteDateDeNaissance)} ` +
                    `à ${naissanceHote}, déclare sur l'honneur héberger à mon domicile ` +
                    `${heberge}, n${hebergeFeminin ? "ée" : "é"} le ` +
                    `${dateEnToutesLettres(r.dateDeNaissance)} à ${naissanceHeberge}, ` +
                    `depuis le ${dateEnToutesLettres(r.depuisLe)}, à l'adresse suivante :`,
            },
            { type: "lignes", lignes: lignesDAdresse(r) },
            {
                type: "paragraphe",
                texte:
                    `J'autorise ${heberge} à domicilier à cette adresse le siège de son ` +
                    "entreprise et à y recevoir son courrier professionnel.",
            },
            {
                type: "paragraphe",
                texte:
                    "Je joins à la présente attestation la copie d'un justificatif de domicile " +
                    "à mon nom, daté de moins de trois mois.",
            },
            {
                type: "paragraphe",
                texte: `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}.`,
            },
            // Pas de libellé au-dessus du paraphe : « (Signature de la personne
            // qui héberge) » est une consigne de formulaire, pas une phrase de
            // lettre, et les parenthèses le trahissent. Le nom porté dessous
            // suffit à dire qui signe.
            { type: "signature", nom: hote },
        ],
    };
}
